import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Alert, Modal, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import FormContainer from '../components/FormContainer';
import BookProgress from '../components/BookProgress';
import Message from '../components/Message';
import Loader from '../components/Loader';
import BookGuideModal from '../components/BookGuideModal';
import { generateBook } from '../actions/genaiActions';
import { useTranslation } from 'react-i18next';
import { GENAI_GENERATE_RESET } from '../constants/genaiConstants';
import { LANGUAGE_ADDON_PRODUCT_ID } from '../constants/productConfig';

function BookInputScreen() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const bookGen = useSelector(state => state.bookGenerate);
    const { loading, error, success, job } = bookGen;

    const { t } = useTranslation();

    // Get order_id and order_item_id from URL (required for payment verification)
    const [orderId, setOrderId] = useState(null);
    const [orderItemId, setOrderItemId] = useState(null);

    // Character state (up to 4 characters)
    const [characters, setCharacters] = useState([
        { name: '', type: '', subtype: '', gender: '', age: '', freetext: '' },
        { name: '', type: '', subtype: '', gender: '', age: '', freetext: '' },
        { name: '', type: '', subtype: '', gender: '', age: '', freetext: '' },
        { name: '', type: '', subtype: '', gender: '', age: '', freetext: '' }
    ]);

    // Story details
    const [storyLocations, setStoryLocations] = useState('');
    const [storyFreetext, setStoryFreetext] = useState('');
    const [language, setLanguage] = useState(searchParams.get('language') || '');
    const [existingJobId, setExistingJobId] = useState(null);
    const [checkingActiveJob, setCheckingActiveJob] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [referenceImageUrls, setReferenceImageUrls] = useState(['', '']);
    const [includeReferenceImagesInBook, setIncludeReferenceImagesInBook] = useState(true);
    const [useRhyme, setUseRhyme] = useState(true);
    const [uploadingReference, setUploadingReference] = useState(false);
    const [referenceUploadError, setReferenceUploadError] = useState('');

    const englishOnly = searchParams.get('english_only') === '1';

    useEffect(() => {
        if (englishOnly) {
            setLanguage('English');
        }
    }, [englishOnly]);

    useEffect(() => {
        dispatch({ type: GENAI_GENERATE_RESET });

        if (!userInfo) {
            const currentPath = `/genai/create_story${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
            navigate('/login?redirect=' + encodeURIComponent(currentPath));
        }

        // Get order_id and order_item_id from URL query parameters
        const orderIdParam = searchParams.get('order_id');
        const orderItemIdParam = searchParams.get('order_item_id');
        
        if (orderIdParam) {
            setOrderId(parseInt(orderIdParam));
        }
        
        if (orderItemIdParam) {
            setOrderItemId(parseInt(orderItemIdParam));
        }
    }, [dispatch, navigate, userInfo, searchParams]);

    useEffect(() => {
        if (success && job) {
            // Navigate to story review screen
            navigate(`/genai/job/${job.job_id}/review-story`);
        }
    }, [success, job, navigate]);

    useEffect(() => {
        let isMounted = true;

        const rerouteToActiveJob = async () => {
            if (!userInfo?.token) {
                return;
            }

            // If order context exists, keep the user on create flow.
            if (orderId || orderItemId) {
                return;
            }

            setCheckingActiveJob(true);
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${userInfo.token}`,
                    },
                };

                const { data } = await axios.get('/api/genai/jobs/', config);
                const jobs = Array.isArray(data) ? data : (data?.results || []);

                const activeJobs = jobs
                    .filter(j => j && j.status !== 'completed' && j.status !== 'failed')
                    .sort((a, b) => {
                        const aTime = new Date(a?.updated_at || a?.created_at || 0).getTime();
                        const bTime = new Date(b?.updated_at || b?.created_at || 0).getTime();
                        return bTime - aTime;
                    });

                if (isMounted && activeJobs.length > 0) {
                    navigate(`/genai/job/${activeJobs[0].job_id}`, { replace: true });
                }
            } catch (err) {
                // If lookup fails, keep default create-story fallback messaging.
            } finally {
                if (isMounted) {
                    setCheckingActiveJob(false);
                }
            }
        };

        rerouteToActiveJob();

        return () => {
            isMounted = false;
        };
    }, [userInfo, orderId, orderItemId, navigate]);

    useEffect(() => {
        if (!error) {
            setExistingJobId(null);
            return;
        }

        const uuidMatch = String(error).match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
        if (uuidMatch) {
            setExistingJobId(uuidMatch[0]);
        } else {
            setExistingJobId(null);
        }
    }, [error]);

    useEffect(() => {
        let isMounted = true;

        const loadOrderCustomization = async () => {
            if (!orderId || !userInfo?.token) {
                return;
            }

            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${userInfo.token}`,
                    },
                };

                const { data } = await axios.get(`/api/orders/${orderId}/`, config);
                const customization = data?.customization || {};
                const orderItems = data?.orderItems || [];

                const languageAddonItem = orderItems.find(
                    (item) => Number(item?.ProductId) === LANGUAGE_ADDON_PRODUCT_ID
                );
                const languageMatch = languageAddonItem?.name?.match(/\(([^)]+)\)/);
                const addonLanguage = languageMatch ? languageMatch[1] : '';
                const languageFromQuery = searchParams.get('language') || '';
                const resolvedLanguage = englishOnly ? 'English' : (languageFromQuery || addonLanguage || 'English');

                if (!isMounted) {
                    return;
                }

                setLanguage(resolvedLanguage);

                if (Array.isArray(customization.referenceImageUrls)) {
                    const urls = [...customization.referenceImageUrls.slice(0, 2)];
                    while (urls.length < 2) urls.push('');
                    setReferenceImageUrls(urls.map((u) => (typeof u === 'string' ? u : '')));
                } else if (customization.referenceImageUrl) {
                    setReferenceImageUrls([customization.referenceImageUrl, '']);
                }

                if (customization.childName) {
                    setCharacters(prev => {
                        if (prev[0]?.name) {
                            return prev;
                        }
                        const next = [...prev];
                        next[0] = {
                            ...next[0],
                            name: customization.childName,
                        };
                        return next;
                    });
                }

                if (customization.storyTheme) {
                    setStoryFreetext(prev => prev || customization.storyTheme);
                }
            } catch (err) {
                // Ignore loading errors here; manual upload still works.
            }
        };

        loadOrderCustomization();

        return () => {
            isMounted = false;
        };
    }, [orderId, userInfo, searchParams, englishOnly]);

    const handleCharacterChange = (index, field, value) => {
        const newCharacters = [...characters];
        newCharacters[index][field] = value;
        if (field === 'type' && value === 'human') {
            newCharacters[index].subtype = 'human';
        }
        setCharacters(newCharacters);
    };

    const handleReferenceImageUpload = async (event, slot) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!orderId || !userInfo?.token) {
            setReferenceUploadError(t('bookInput.referenceUploadNeedsOrder'));
            return;
        }

        setReferenceUploadError('');
        setUploadingReference(true);

        try {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('slot', String(slot));

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            const { data } = await axios.post(
                `/api/orders/${orderId}/customization-image/`,
                formData,
                config,
            );

            if (Array.isArray(data?.referenceImageUrls)) {
                const urls = [...data.referenceImageUrls.slice(0, 2)];
                while (urls.length < 2) urls.push('');
                setReferenceImageUrls(urls.map((u) => (typeof u === 'string' ? u : '')));
            } else if (data?.referenceImageUrl) {
                setReferenceImageUrls((prev) => {
                    const next = [...prev];
                    next[slot - 1] = data.referenceImageUrl;
                    return next;
                });
            }
        } catch (err) {
            setReferenceUploadError(err.response?.data?.detail || t('bookInput.referenceUploadFailed'));
        } finally {
            setUploadingReference(false);
            event.target.value = '';
        }
    };

    const submitHandler = (e) => {
        e.preventDefault();

        // Validate order_id and order_item_id are present (required for payment verification)
        if (!orderId) {
            alert(t('bookInput.alertNoOrder'));
            navigate('/products');
            return;
        }
        
        if (!orderItemId) {
            alert(t('bookInput.alertNoItem'));
            navigate(`/order/${orderId}`);
            return;
        }

        // Filter out empty characters
        const validCharacters = characters.filter(char => char.name.trim() !== '');

        if (validCharacters.length === 0) {
            alert(t('bookInput.alertNoChar'));
            return;
        }

        if (!storyFreetext.trim()) {
            alert(t('bookInput.alertNoTheme'));
            return;
        }

        const hasAtLeastOneReference = referenceImageUrls.some((url) => typeof url === 'string' && url.trim());
        if (!hasAtLeastOneReference) {
            const continueWithoutPhoto = window.confirm(t('bookInput.referenceMissingConfirm'));
            if (!continueWithoutPhoto) {
                return;
            }
        }

        setShowConfirmModal(true);
    };

    const confirmSubmit = () => {
        setShowConfirmModal(false);

        const validCharacters = characters.filter(char => char.name.trim() !== '');

        const bookData = {
            order_id: orderId,
            order_item_id: orderItemId,
            characters: validCharacters,
            story_locations: storyLocations,
            story_freetext: storyFreetext,
            language: language || 'English',
            include_reference_images_in_book: includeReferenceImagesInBook,
            use_rhyme: useRhyme,
        };

        dispatch(generateBook(bookData));
    };

    return (
        <div>
            <BookProgress step1 />
            
            <FormContainer>
                <h1>{t('bookInput.title')}</h1>
                <p className="text-muted">
                    {t('bookInput.subtitle')}
                </p>
                <Alert variant="info" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span>{t('bookInput.layoutIntro')}</span>
                    <Button variant="outline-info" size="sm" onClick={() => setShowGuideModal(true)}>
                        {t('common.viewBookLayoutGuide')}
                    </Button>
                </Alert>

                <BookGuideModal show={showGuideModal} onHide={() => setShowGuideModal(false)} />

                {checkingActiveJob && !orderId && !orderItemId ? (
                    <Alert variant="info">
                        <Loader />
                    </Alert>
                ) : orderId && orderItemId ? (
                    <Alert variant="success">
                        <i className="fas fa-check-circle me-2"></i>
                        <strong>{t('bookInput.orderVerified', { orderId, itemId: orderItemId })}</strong> {t('bookInput.paymentConfirmed')}
                    </Alert>
                ) : orderId ? (
                    <Alert variant="warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>{t('bookInput.missingOrderItem')}</strong> {t('bookInput.selectFromOrder')}{' '}
                        <Alert.Link
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/order/${orderId}`);
                            }}
                        >
                            {t('bookInput.yourOrder')}
                        </Alert.Link>
                    </Alert>
                ) : (
                    <Alert variant="warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>{t('bookInput.noOrder')}</strong> {t('bookInput.mustPurchase')}{' '}
                        <Alert.Link
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/products');
                            }}
                        >
                            {t('bookInput.goToProducts')}
                        </Alert.Link>
                    </Alert>
                )}

                {loading && <Loader />}
                {error && <Message variant='danger'>{error}</Message>}
                {existingJobId && (
                    <Alert variant="info">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span>
                                {t('bookInput.existingJob')}
                            </span>
                            <div>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => navigate(`/genai/job/${existingJobId}/review-story`)}
                                >
                                    {t('bookInput.resumeJob')}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate('/genai/jobs')}
                                >
                                    {t('bookInput.viewAllJobs')}
                                </Button>
                            </div>
                        </div>
                    </Alert>
                )}

                {orderId && orderItemId && <Form onSubmit={submitHandler}>
                    <h3 className="mt-4 mb-3">{t('bookInput.referenceImageTitle')}</h3>
                    <Alert variant="warning" className="mb-3">
                        <strong>{t('bookInput.referenceImageHighlight')}</strong>
                    </Alert>
                    <div className="small text-muted mb-2">{t('bookInput.referenceImageCountNote')}</div>

                    <Card className="mb-4 border-primary p-3">
                        <Form.Group controlId="referenceImage1" className="mb-3">
                            <Form.Label className="fw-bold">{t('bookInput.referenceImageLabel1')}</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleReferenceImageUpload(e, 1)}
                                disabled={uploadingReference}
                            />
                        </Form.Group>

                        <Form.Group controlId="referenceImage2" className="mb-0">
                            <Form.Label className="fw-bold">{t('bookInput.referenceImageLabel2')}</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleReferenceImageUpload(e, 2)}
                                disabled={uploadingReference}
                            />
                            <Form.Text className="text-muted">
                                {t('bookInput.referenceImageHelp')}
                            </Form.Text>
                            {uploadingReference && (
                                <div className="mt-2 text-muted">{t('bookInput.referenceUploading')}</div>
                            )}
                            {referenceUploadError && (
                                <Message variant="danger">{referenceUploadError}</Message>
                            )}
                            <div className="mt-3">
                                <div className="small text-muted mb-2">{t('bookInput.referenceImagePreview')}</div>
                                <Row>
                                    {[0, 1].map((idx) => (
                                        <Col md={6} key={idx} className="mb-2">
                                            {referenceImageUrls[idx] ? (
                                                <img
                                                    src={referenceImageUrls[idx]}
                                                    alt={`${t('bookInput.referenceImageAlt')} ${idx + 1}`}
                                                    style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '8px' }}
                                                />
                                            ) : (
                                                <div className="border rounded p-3 text-muted text-center" style={{ minHeight: '120px' }}>
                                                    {t('bookInput.referenceImageEmptySlot', { slot: idx + 1 })}
                                                </div>
                                            )}
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </Form.Group>

                        <Form.Group controlId="includeReferenceImagesInBook" className="border-top mt-3 pt-3">
                            <Form.Check
                                type="switch"
                                label={t('bookInput.includeReferenceImagesLabel')}
                                checked={includeReferenceImagesInBook}
                                onChange={(event) => setIncludeReferenceImagesInBook(event.target.checked)}
                            />
                            <Form.Text className="text-muted">
                                {t('bookInput.includeReferenceImagesHelp')}
                            </Form.Text>
                        </Form.Group>
                    </Card>

                    {/* Characters Section */}
                    <h3 className="mt-4 mb-3">{t('bookInput.charactersTitle')}</h3>
                    <Alert variant="info">
                        <span dangerouslySetInnerHTML={{ __html: t('bookInput.charactersTip') }} />
                    </Alert>

                    {characters.map((char, index) => (
                        <Card key={index} className="mb-3 p-3">
                            <h5>{index === 0 ? t('bookInput.mainChar') : index === 1 ? t('bookInput.secondaryChar') : t('bookInput.characterN', { n: index + 1 })}</h5>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group controlId={`char${index}Name`} className="mb-2">
                                        <Form.Label>{index === 0 ? t('bookInput.charNameRequired') : t('bookInput.charName')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('bookInput.charNamePlaceholder')}
                                            value={char.name}
                                            onChange={(e) => handleCharacterChange(index, 'name', e.target.value)}
                                            required={index === 0}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId={`char${index}Type`} className="mb-2">
                                        <Form.Label>{t('bookInput.charType')}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={char.type}
                                            onChange={(e) => handleCharacterChange(index, 'type', e.target.value)}
                                        >
                                            <option value="">{t('bookInput.selectType')}</option>
                                            <option value="human">{t('bookInput.typeHuman')}</option>
                                            <option value="animal">{t('bookInput.typeAnimal')}</option>
                                            <option value="anthropomorphic_animal">{t('bookInput.typeAnthroAnimal')}</option>
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                {char.type !== 'human' && (
                                <Col md={6}>
                                    <Form.Group controlId={`char${index}Subtype`} className="mb-2">
                                        <Form.Label>{t('bookInput.charSubtype')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('bookInput.subtypePlaceholder')}
                                            value={char.subtype}
                                            onChange={(e) => handleCharacterChange(index, 'subtype', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            {t('bookInput.subtypeHelp')}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                )}
                                <Col md={3}>
                                    <Form.Group controlId={`char${index}Gender`} className="mb-2">
                                        <Form.Label>{t('bookInput.charGender')}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={char.gender}
                                            onChange={(e) => handleCharacterChange(index, 'gender', e.target.value)}
                                        >
                                            <option value="">{t('bookInput.selectGender')}</option>
                                            <option value="male">{t('bookInput.male')}</option>
                                            <option value="female">{t('bookInput.female')}</option>
                                            <option value="non-binary">{t('bookInput.nonBinary')}</option>
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group controlId={`char${index}Age`} className="mb-2">
                                        <Form.Label>{t('bookInput.charAge')}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={char.age}
                                            onChange={(e) => handleCharacterChange(index, 'age', e.target.value)}
                                        >
                                            <option value="">{t('bookInput.selectGender')}</option>
                                            <option value="baby">{t('bookInput.ageBaby')}</option>
                                            <option value="toddler">{t('bookInput.ageToddler')}</option>
                                            <option value="child">{t('bookInput.ageChild')}</option>
                                            <option value="teen">{t('bookInput.ageTeen')}</option>
                                            <option value="adult">{t('bookInput.ageAdult')}</option>
                                            <option value="elderly">{t('bookInput.ageElderly')}</option>
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group controlId={`char${index}Freetext`} className="mb-2">
                                <Form.Label>{t('bookInput.charDescription')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder={t('bookInput.descPlaceholder')}
                                    value={char.freetext}
                                    onChange={(e) => handleCharacterChange(index, 'freetext', e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    {t('bookInput.descHelp')}
                                </Form.Text>
                            </Form.Group>
                        </Card>
                    ))}

                    {/* Story Details Section */}
                    <h3 className="mt-4 mb-3">{t('bookInput.storyDetails')}</h3>

                    <Form.Group className="mb-4" controlId="storyStyle">
                        <Form.Label className="fw-bold">{t('bookInput.storyStyleTitle')}</Form.Label>
                        <div className="small text-muted mb-2">{t('bookInput.storyStyleHelp')}</div>
                        <ButtonGroup className="w-100">
                            <ToggleButton
                                id="story-style-rhyme"
                                type="radio"
                                variant={useRhyme ? 'primary' : 'outline-primary'}
                                name="storyStyle"
                                value="rhyme"
                                checked={useRhyme}
                                onChange={() => setUseRhyme(true)}
                            >
                                {t('bookInput.storyStyleRhyme')}
                            </ToggleButton>
                            <ToggleButton
                                id="story-style-prose"
                                type="radio"
                                variant={!useRhyme ? 'primary' : 'outline-primary'}
                                name="storyStyle"
                                value="prose"
                                checked={!useRhyme}
                                onChange={() => setUseRhyme(false)}
                            >
                                {t('bookInput.storyStyleProse')}
                            </ToggleButton>
                        </ButtonGroup>
                        <Form.Text className="text-muted d-block mt-2">
                            {t(useRhyme ? 'bookInput.storyStyleRhymeHelp' : 'bookInput.storyStyleProseHelp')}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group controlId="storyLocations" className="mb-3">
                        <Form.Label>{t('bookInput.storyLocation')}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('bookInput.locationPlaceholder')}
                            value={storyLocations}
                            onChange={(e) => setStoryLocations(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            {t('bookInput.locationHelp')}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group controlId="storyFreetext" className="mb-3">
                        <Form.Label>{t('bookInput.storyTheme')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder={t('bookInput.themePlaceholder')}
                            value={storyFreetext}
                            onChange={(e) => setStoryFreetext(e.target.value)}
                            required
                            maxLength={1000}
                        />
                        <Form.Text className="text-muted">
                            {t('bookInput.charCount', { count: storyFreetext.length })}
                        </Form.Text>
                    </Form.Group>

                    {/* Pre-submit checklist */}
                    <Card className="my-4 border-warning">
                        <Card.Body>
                            <h6 className="fw-bold mb-3">{t('bookInput.checklistTitle')}</h6>
                            <ol className="mb-0" style={{ paddingLeft: '1.2rem', lineHeight: '1.8' }}>
                                <li dangerouslySetInnerHTML={{ __html: t('bookInput.check1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t('bookInput.check2') }} />
                                <li dangerouslySetInnerHTML={{ __html: t('bookInput.check3') }} />
                            </ol>
                        </Card.Body>
                    </Card>

                    <div className="d-grid gap-2">
                        <Button 
                            type="submit" 
                            variant="primary" 
                            size="lg"
                            disabled={loading}
                        >
                            {loading ? t('bookInput.creatingStory') : t('bookInput.createStory')}
                        </Button>

                    <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{t('bookInput.confirmTitle')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>{t('bookInput.confirmBody')}</p>
                            <p>{t('bookInput.confirmCheck')}</p>
                            <dl className="mb-0">
                                <dt>{t('bookInput.confirmStoryStyle')}</dt>
                                <dd>{t(useRhyme ? 'bookInput.storyStyleRhyme' : 'bookInput.storyStyleProse')}</dd>
                                <dt>{t('bookInput.confirmReferenceImages')}</dt>
                                <dd className="mb-0">
                                    {t(includeReferenceImagesInBook
                                        ? 'bookInput.confirmReferenceImagesIncluded'
                                        : 'bookInput.confirmReferenceImagesExcluded')}
                                </dd>
                            </dl>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                                {t('bookInput.goBack')}
                            </Button>
                            <Button variant="primary" onClick={confirmSubmit}>
                                {t('bookInput.confirmSubmit')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    </div>
                </Form>}
            </FormContainer>
        </div>
    );
}

export default BookInputScreen;
